import {
  AlignmentType,
  BorderStyle,
  Document,
  ImageRun,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import { decamelize } from "fast-case";
import { saveAs } from "file-saver";

interface Address {
  address_line1: string;
  city: string;
  state: string;
  postal_code: string;
  country_code: string;
  address_line2?: string;
  address_line3?: string;
  unit_number?: string;
}

interface Individual {
  id: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  email_address: string;
  mailing_address: Address;
  telephone_number: string;
  tax_reference_number: string;
  date_of_birth: string;
  percentage_ownership: number;
}

interface KycProfile {
  description_of_business_nature: string;
  business_jurisdictions: string[];
  funds_send_receive_jurisdictions: string[];
  engage_in_activities: string[];
  regulated_status: string;
}

interface InvestmentProfile {
  primary_source_of_funds: string;
  usd_value_of_fiat: string;
  usd_value_of_crypto: string;
  monthly_crypto_investment_deposit: string;
  monthly_investment_deposit: string;
  monthly_crypto_deposits: string;
  monthly_deposits: string;
  monthly_crypto_investment_withdrawal: string;
  monthly_investment_withdrawal: string;
  monthly_crypto_withdrawals: string;
  monthly_withdrawals: string;
}

interface UserData {
  id: string;
  status: string;
  telephone_number: string;
  email_address: string;
  registered_address: Address;
  physical_address: Address;
  kyc_profile: KycProfile;
  investment_profile: InvestmentProfile;
  registered_name: string;
  trading_name: string;
  registered_number: string;
  website_address: string;
  state_of_incorporation: string;
  country_of_incorporation: string;
  corporate_entity_type: string;
  established_on: string;
  naics: string;
  naics_description: string;
  individuals: Individual[];
}

export function generateDocxCustomerData(data: UserData, t: any) {
  const keysToIgnore = [
    "physical_address",
    "registered_address",
    "kyc_profile",
    "investment_profile",
    "individuals",
  ];

  const base64image =
    "data:image/png;base64,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";

  const paragraphs = [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 0, before: 400 },
      children: [
        new ImageRun({
          data: base64image,
          transformation: {
            width: 240,
            height: 50,
          },
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 200, before: 400 },
      children: [
        new TextRun({
          text: `Customer Information\n`,
          bold: true,
          size: 36,
        }),
      ],
    }),
    ...Object.entries(data).map(([key, value]) => {
      if (!keysToIgnore.includes(key)) {
        return new Paragraph({
          spacing: { after: 0, before: 100 },
          children: [
            new TextRun({
              text: `${t(`management/customer:details.${key}`)}: ${value}\n`,
              size: 28,
            }),
          ],
        });
      }
    }),
  ];

  if (data.physical_address) {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 200, before: 300 },
        children: [
          new TextRun({
            text: `Physical Address:\n`,
            bold: true,
            size: 36,
          }),
        ],
      }),
      ...Object.entries(data.physical_address).map(
        ([key, value]) =>
          new Paragraph({
            spacing: { after: 0, before: 100 },
            children: [
              new TextRun({
                text: `${t(`management/customer:details.${key}`)}: ${value}\n`,
                size: 28,
              }),
            ],
          }),
      ),
    );
  }

  if (data.registered_address) {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 200, before: 300 },
        children: [
          new TextRun({
            text: `Registered Address:\n`,
            bold: true,
            size: 36,
          }),
        ],
      }),
    );

    Object.entries(data.registered_address).forEach(([key, value]) => {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 0, before: 100 },
          children: [
            new TextRun({
              text: `${t(`management/customer:details.${key}`)}: ${value}\n`,
              size: 28,
            }),
          ],
        }),
      );
    });
  }

  if (data.kyc_profile) {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 200, before: 400 },
        children: [
          new TextRun({
            text: `KYC Profile:\n`,
            bold: true,
            size: 36,
          }),
        ],
      }),
      ...Object.entries(data.kyc_profile).map(
        ([key, value]) =>
          new Paragraph({
            spacing: { after: 0, before: 100 },
            children: [
              new TextRun({
                text: `${t(`management/customer:details.${key}`)}: ${
                  Array.isArray(value) ? value.join(", ") : value
                }\n`,
                size: 28,
              }),
            ],
          }),
      ),
    );
  }

  if (data.investment_profile) {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 200, before: 300 },
        children: [
          new TextRun({
            text: `Investment Profile:\n`,
            bold: true,
            size: 36,
          }),
        ],
      }),
      ...Object.entries(data.investment_profile).map(
        ([key, value]) =>
          new Paragraph({
            spacing: { after: 0, before: 100 },
            children: [
              new TextRun({
                text: `${t(`management/customer:details.${key}`)}: ${
                  Array.isArray(value)
                    ? value.join(", ")
                    : t(`enums:InvestmentProfile.${value}`)
                }\n`,
                size: 28,
              }),
            ],
          }),
      ),
    );
  }

  if (data.individuals && data.individuals.length > 0) {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 200, before: 300 },
        children: [
          new TextRun({
            text: `Individuals:\n`,
            bold: true,
            size: 36,
          }),
        ],
      }),
      ...data.individuals.flatMap((individual) => {
        const individualParagraphs = [];
        Object.entries(individual).forEach(([key, value]) => {
          if (key === "mailing_address" && typeof value === "object") {
            Object.entries(value).forEach(([addrKey, addrValue]) => {
              individualParagraphs.push(
                new Paragraph({
                  spacing: { after: 0, before: 100 },
                  children: [
                    new TextRun({
                      text: `${t(
                        `management/customer:details.${addrKey}`,
                      )}: ${addrValue}\n`,
                      size: 28,
                    }),
                  ],
                }),
              );
            });
          } else {
            individualParagraphs.push(
              new Paragraph({
                spacing: { after: 0, before: 100 },
                children: [
                  new TextRun({
                    text: `${t(`management/customer:details.${key}`)}: ${
                      Array.isArray(value) ? value.join(", ") : value
                    }\n`,
                    size: 28,
                  }),
                ],
              }),
            );
          }
        });

        if (individualParagraphs.length > 0) {
          const lastIndex = individualParagraphs.length - 1;
          individualParagraphs[lastIndex].border = {
            bottom: {
              color: "auto",
              space: 1,
              style: BorderStyle.SINGLE,
              size: 6,
            },
          };
        }

        individualParagraphs.push(
          new Paragraph({
            spacing: { after: 400 },
            border: {
              bottom: {
                color: "auto",
                space: 1,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
          }),
        );

        return individualParagraphs;
      }),
    );
  }
  const doc = new Document({
    sections: [
      {
        children: paragraphs,
      },
    ],
  });

  Packer.toBlob(doc).then((blob) => {
    saveAs(blob, "customerdata.docx");
  });
}

export function generateDocxCounterpartiesData(data: any, t: any) {
  const base64image =
    "data:image/png;base64,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";

  const paragraphs = [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 0, before: 400 },
      children: [
        new ImageRun({
          data: base64image,
          transformation: {
            width: 240,
            height: 50,
          },
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 200, before: 400 },
      children: [
        new TextRun({
          text: `Customer Information\n`,
          bold: true,
          size: 36,
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 0, before: 100 },
      children: [
        new TextRun({
          text: `Customer ID: ${data.application?.id}`,
          size: 28,
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 0, before: 100 },
      children: [
        new TextRun({
          text: `Customer Name: ${
            data.application?.customerDetailsCorporation.registeredName
              ? data.application?.customerDetailsCorporation.registeredName
              : data.application?.customerDetailsIndividual.firstName
          }`,
          size: 28,
        }),
      ],
    }),
  ];

  if (data.counterParties && data.counterParties.length > 0) {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 200, before: 300 },
        children: [
          new TextRun({
            text: `Counterparties:\n`,
            bold: true,
            size: 36,
          }),
        ],
      }),
      ...data.counterParties.flatMap((counterparty) => {
        const counterpartyParagraphs = [];
        Object.entries(counterparty).forEach(([key, value]) => {
          if (value === null || value === "") {
            return;
          }
          if (
            (key === "profile" || key === "accountInformation") &&
            typeof value === "object"
          ) {
            Object.entries(value).forEach(([key, value]) => {
              if (value === null || value === "") {
                return;
              }
              if (
                (key === "address" || key === "institutionAddress") &&
                typeof value === "object"
              ) {
                Object.entries(value).forEach(([addrKey, addrValue]) => {
                  if (addrValue === null || addrValue === "") {
                    return;
                  } else if (addrKey === "id") {
                    return;
                  }
                  counterpartyParagraphs.push(
                    new Paragraph({
                      spacing: { after: 0, before: 100 },
                      children: [
                        new TextRun({
                          text: `${
                            key === "institutionAddress"
                              ? "Institution Address"
                              : key === "address"
                                ? "Address"
                                : key
                          } - ${t(
                            `management/customer:details.${decamelize(
                              addrKey,
                            )}`,
                          )}: ${addrValue}\n`,
                          size: 28,
                        }),
                      ],
                    }),
                  );
                });
              } else {
                if (value === null || value === "") {
                  return;
                }
                counterpartyParagraphs.push(
                  new Paragraph({
                    spacing: { after: 0, before: 100 },
                    children: [
                      new TextRun({
                        text: `${t(
                          `management/customer:details.${decamelize(key)}`,
                        )}: ${value}\n`,
                        size: 28,
                      }),
                    ],
                  }),
                );
              }
            });
          } else {
            counterpartyParagraphs.push(
              new Paragraph({
                spacing: { after: 0, before: 100 },
                children: [
                  new TextRun({
                    text: `${t(
                      `management/customer:details.${decamelize(key)}`,
                    )}: ${Array.isArray(value) ? value.join(", ") : value}\n`,
                    size: 28,
                  }),
                ],
              }),
            );
          }
        });

        if (counterpartyParagraphs.length > 0) {
          const lastIndex = counterpartyParagraphs.length - 1;
          counterpartyParagraphs[lastIndex].border = {
            bottom: {
              color: "auto",
              space: 1,
              style: BorderStyle.SINGLE,
              size: 6,
            },
          };
        }
        counterpartyParagraphs.push(
          new Paragraph({
            spacing: { after: 400 },
            border: {
              bottom: {
                color: "auto",
                space: 1,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
          }),
        );

        return counterpartyParagraphs;
      }),
    );
  }
  const doc = new Document({
    sections: [
      {
        children: paragraphs,
      },
    ],
  });

  Packer.toBlob(doc).then((blob) => {
    saveAs(blob, `counterparties_${data.id}.docx`);
  });
}
